import React, {memo, FC, useEffect} from "react";
import {timeRanges, timeRangeTitles} from "./timeRanges";
import {Moment} from "moment";
import {
    useGetOnlineBookingsByTimeRangeCountQuery,
    useGetOnlineBookingsByTimeRangeSeriesQuery,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {grey, orange} from "@ant-design/colors";
import css from "./Cards.less";
import {Spin, Statistic, Modal, Button} from "antd";
import ShowBookings from "./Elements/ShowBookings";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import Sparkline from "./Sparkline";

// Anzahl der Verlaufs-Buckets für die Sparkline pro Kachel.
const SPARKLINE_BUCKETS = 30;

type CardBookingsStatsProps = {
    timeRangeString: keyof typeof timeRanges;
    refetchTimeStamp?: number;
};

const CardBookingsStats: FC<CardBookingsStatsProps> = ({timeRangeString, refetchTimeStamp}) => {
    const [timeRange, setTimeRange] = React.useState<[Moment, Moment]>(timeRanges[timeRangeString]);

    const {data, loading, refetch} = useGetOnlineBookingsByTimeRangeCountQuery({
        variables: {
            startDateTime: timeRange[0].toISOString(),
            endDateTime: timeRange[1].toISOString(),
        },
        fetchPolicy: "network-only",
    });

    const {data: seriesData, refetch: refetchSeries} = useGetOnlineBookingsByTimeRangeSeriesQuery({
        variables: {
            startDateTime: timeRange[0].toISOString(),
            endDateTime: timeRange[1].toISOString(),
            buckets: SPARKLINE_BUCKETS,
        },
        fetchPolicy: "network-only",
    });

    const [modalOpen, setModalOpen] = React.useState(false);

    useEffect(() => {
        refetch();
        refetchSeries();
    }, [refetchTimeStamp]);

    const count = data?.getOnlineBookingsByTimeRangeCount;
    const series = seriesData?.getOnlineBookingsByTimeRangeSeries;
    // @ts-ignore
    // const translatedTimeRange = i18n.containers.dashboard.DashboardOverview.cards.bookings[
    //     timeRangeString
    // ]();

    const translatedTimeRange = timeRangeTitles[timeRangeString];

    const Title = () => {
        return <div /* style={{borderLeft: `2px solid ${grey[1]}`}} */>{translatedTimeRange}</div>;
    };

    type StatsCSSProperties = React.CSSProperties & {
        "--color": string;
        "--colorLight": string;
    };

    const actualStyle: StatsCSSProperties = {
        "--color": orange[5],
        "--colorLight": orange[2],
    };

    if (loading) {
        return (
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    padding: 10,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <>
            <DraggableModal
                data-testid="card-bookings-stats-modal"
                draggable={true}
                boundsPadding={500}
                title={`Onlineanmeldungen ${translatedTimeRange}`}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                width={"90vw"}
                destroyOnClose
                footer={null}
                bodyStyle={{padding: 5}}
            >
                <ShowBookings timeRangeString={timeRangeString} />
            </DraggableModal>
            <Statistic
                value={count}
                title={<Title />}
                loading={loading}
                className={css.statisticCard}
                style={actualStyle}
                groupSeparator="."
                decimalSeparator=","
                valueStyle={{
                    fontSize: "3em",
                    fontWeight: 600,
                    opacity: 0.8,
                    paddingRight: 5,
                    textAlign: "right",
                }}
            />
            <Button
                onClick={() => setModalOpen(true)}
                block
                type="link"
                size="small"
                className={css.showBookingsButton}
            >
                {"Buchungen anzeigen"}
            </Button>
            {series && series.length > 1 ? (
                <div className={css.sparkline} aria-hidden="true">
                    <Sparkline values={series} />
                </div>
            ) : null}
        </>
    );
};

export default memo(CardBookingsStats);
